import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import API_ENDPOINTS from "../apiConfig";
import useOutsideClick from "./helper/UseOutsideClick";
import "bootstrap/dist/css/bootstrap.min.css";
import "../style/ezentry.css";
import { authFetch } from "../utils/authFetch";
import { useSite } from "../context/SiteContext";

const ResponsiveGateControl = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [gates, setGates] = useState([]);
  const [gateStatus, setGateStatus] = useState({});
  const panelRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const { selectedArea } = useSite();
  const area = selectedArea;

  useEffect(() => {
    if (area?.gates) setGates(area.gates);
  }, [area]);

  useOutsideClick([toggleBtnRef, panelRef], () => !embedded && setIsOpen(false));

  const requestLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject({ code: 2, message: "Geolocation not supported" });
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: 10000,
        maximumAge: 30000,
      });
    });

  const handleGateAction = async (gateObj, action) => {
    setGateStatus((prev) => ({ ...prev, [gateObj.id]: "loading" }));

    let lat = null;
    let lng = null;

    if (gateObj.has_proximity) {
      const role = localStorage.getItem("actualUserRole");
      if (role !== "site_manager") {
        // Show one-time explanation before triggering the native browser prompt
        if (!sessionStorage.getItem("geo_explained")) {
          sessionStorage.setItem("geo_explained", "1");
          // Small delay so the user sees the status change before the prompt appears
          await new Promise((r) => setTimeout(r, 100));
        }

        setGateStatus((prev) => ({ ...prev, [gateObj.id]: "locating" }));
        try {
          const pos = await requestLocation();
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch (err) {
          if (err.code === 1 || !navigator.geolocation) {
            // Permanently blocked: user denied, or HTTP context (geolocation API unavailable)
            setGateStatus((prev) => ({ ...prev, [gateObj.id]: "location_denied" }));
          } else {
            // Temporary failure (timeout, position unavailable) — allow retry
            setGateStatus((prev) => ({ ...prev, [gateObj.id]: "location_error" }));
            setTimeout(
              () => setGateStatus((prev) => ({ ...prev, [gateObj.id]: "idle" })),
              3000
            );
          }
          return;
        }
      }
    }

    setGateStatus((prev) => ({ ...prev, [gateObj.id]: "loading" }));

    try {
      const body = { action };
      if (lat !== null) { body.lat = lat; body.lng = lng; }

      const response = await authFetch(
        `${API_ENDPOINTS.getareas}/${area.id}/gates/${gateObj.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      if (response.status === 403) {
        setGateStatus((prev) => ({ ...prev, [gateObj.id]: "too_far" }));
        setTimeout(
          () => setGateStatus((prev) => ({ ...prev, [gateObj.id]: "idle" })),
          3000
        );
        return;
      }

      if (response.status === 422) {
        // Backend required location but none was sent — geolocation unavailable (e.g. HTTP)
        setGateStatus((prev) => ({ ...prev, [gateObj.id]: "location_denied" }));
        return;
      }

      if (!response.ok) throw new Error("Failed to control gate");

      setGateStatus((prev) => ({ ...prev, [gateObj.id]: "success" }));
      setTimeout(
        () => setGateStatus((prev) => ({ ...prev, [gateObj.id]: "idle" })),
        1500
      );
    } catch (error) {
      console.error(error);
      setGateStatus((prev) => ({ ...prev, [gateObj.id]: "error" }));
      setTimeout(
        () => setGateStatus((prev) => ({ ...prev, [gateObj.id]: "idle" })),
        1500
      );
    }
  };

  const renderGateTiles = () => {
    if (!gates || gates.length === 0) {
      return <div className="text-muted">{t("no gates")}</div>;
    }

    return (
      <div className="d-flex flex-wrap gap-3 justify-content-center">
        {gates.map((gate) => {
          const status = gateStatus[gate.id] || "idle";

          const statusStyles = {
            idle: "border-light",
            loading: "bg-warning bg-opacity-10 border-warning",
            locating: "bg-warning bg-opacity-10 border-warning",
            success: "bg-success bg-opacity-10 border-success",
            error: "bg-danger bg-opacity-10 border-danger",
            too_far: "bg-danger bg-opacity-10 border-danger",
            location_denied: "bg-danger bg-opacity-10 border-danger",
            location_error: "bg-danger bg-opacity-10 border-danger",
          };

          const iconMap = {
            idle: "bi bi-door-closed",
            success: "bi bi-check-circle text-success",
            error: "bi bi-x-circle text-danger",
            too_far: "bi bi-geo-alt text-danger",
            location_denied: "bi bi-geo-slash text-danger",
            location_error: "bi bi-exclamation-circle text-danger",
          };

          const isBlocked = status === "loading" || status === "locating";
          const isPersistent = status === "location_denied";

          return (
            <div
              key={gate.id}
              className={`gate-tile card shadow-sm border-2 ${statusStyles[status]}`}
              style={{
                width: "180px",
                cursor: isBlocked || isPersistent ? "not-allowed" : "pointer",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
              }}
              onClick={() => !isBlocked && !isPersistent && handleGateAction(gate, "open")}
            >
              <div className="card-body text-center">
                <div className="mb-2">
                  {status === "loading" || status === "locating" ? (
                    <div className="spinner-border spinner-border-sm text-warning"></div>
                  ) : (
                    <i className={`${iconMap[status] || iconMap.idle} fs-3`}></i>
                  )}
                </div>
                <h6 className="fw-semibold mb-1">{gate.name}</h6>
                <small className="text-muted d-block mb-2">
                  {status === "idle" && t("Tap to open")}
                  {status === "loading" && t("Opening...")}
                  {status === "locating" && t("Getting location")}
                  {status === "success" && t("Opened")}
                  {status === "error" && t("Error")}
                  {status === "too_far" && t("Too far from gate")}
                  {status === "location_error" && t("Location unavailable")}
                  {status === "location_denied" && (
                    <span>
                      {t("Location access blocked")}
                      <br />
                      <span style={{ fontSize: "0.75em" }}>{t("Re-enable location hint")}</span>
                    </span>
                  )}
                </small>
                <button
                  className="btn ez-button-full btn-sm"
                  disabled={isBlocked || isPersistent}
                >
                  {status === "loading"
                    ? t("Opening...")
                    : status === "locating"
                    ? t("Getting location")
                    : status === "success"
                    ? t("Opened")
                    : status === "too_far"
                    ? t("Too far from gate")
                    : status === "location_denied"
                    ? t("Location access blocked")
                    : status === "location_error"
                    ? t("Retry")
                    : status === "error"
                    ? t("Retry")
                    : t("Open")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // --- Embedded Mode: show tiles directly ---
  if (embedded || (!embedded && localStorage.getItem("actualUserRole") === "gate_opener")) {
    return <div ref={panelRef}>{renderGateTiles()}</div>;
  }

  // --- Floating Mode: button + popup panel ---
  return (
    <>
      <button
        ref={toggleBtnRef}
        className="gate-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className="bi bi-door-open"></i>
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          className="gate-panel shadow-lg rounded-3 p-3 bg-light"
        >
          {renderGateTiles()}
        </div>
      )}
    </>
  );
};

export default ResponsiveGateControl;
